import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import * as Types from 'types'
import { compose } from 'redux'
import { lpForm } from 'lp-form'
import { modifyProps } from 'lp-hoc'
import {
  Field,
  FieldArray,
  propTypes as formPropTypes,
  SubmissionError,
} from 'redux-form'
import {
  Select,
  Input,
  SubmitButton,
  InputError,
  generateInputErrorId,
} from 'lp-components'
import { HiddenLabel } from 'components'
import {
  persistSubmitSucceeded,
  replaceResources,
  serializeOptions,
  getNoneTypeId,
  getOtherTypeId,
} from 'utils'
import { get, keyBy } from 'lodash'

const propTypes = {
  ...formPropTypes,
  disciplines: PropTypes.arrayOf(Types.discipline).isRequired,
  frequencyTypes: PropTypes.arrayOf(Types.communityEnumerable).isRequired,
  durationTypes: PropTypes.arrayOf(Types.communityEnumerable).isRequired,
  frequencyColumnHeader: PropTypes.string.isRequired,
  durationColumnHeader: PropTypes.string.isRequired,
}

const defaultProps = {}

const FORM_NAME = 'school-instructional-time'
const TABLE_GROUP_ERROR_NAME = `${FORM_NAME}-table-group`

// Select elements always hand back strings, but the API expects record ids
function parseSelectedId(value) {
  if (value === '' || value === null || value === undefined) return null
  return Number(value)
}

function InstructionalTimeRow({
  field,
  row,
  discipline,
  frequencyOptions,
  durationOptions,
  frequencyColumnHeader,
  durationColumnHeader,
  otherFrequencyTypeId,
  otherDurationTypeId,
  onFrequencyChange,
}) {
  const showFrequencyOtherText =
    !!otherFrequencyTypeId && row.frequencyTypeId === otherFrequencyTypeId
  const showDurationOtherText =
    !!otherDurationTypeId && row.durationTypeId === otherDurationTypeId

  return (
    <tr>
      <th scope="row">{discipline.displayName}</th>
      <td>
        <Field
          name={field + '.frequencyTypeId'}
          label={`${discipline.displayName}, ${frequencyColumnHeader}`}
          labelComponent={HiddenLabel}
          placeholder={frequencyColumnHeader}
          options={frequencyOptions}
          parse={parseSelectedId}
          onChange={(_event, value) => onFrequencyChange(field, value)}
          component={Select}
        />
        {showFrequencyOtherText && (
          <Field
            name={field + '.frequencyOtherText'}
            label={`${discipline.displayName}, describe other frequency`}
            labelComponent={HiddenLabel}
            placeholder="Description..."
            component={Input}
          />
        )}
      </td>
      <td>
        <Field
          name={field + '.durationTypeId'}
          label={`${discipline.displayName}, ${durationColumnHeader}`}
          labelComponent={HiddenLabel}
          placeholder={durationColumnHeader}
          options={durationOptions}
          parse={parseSelectedId}
          component={Select}
        />
        {showDurationOtherText && (
          <Field
            name={field + '.durationOtherText'}
            label={`${discipline.displayName}, describe other duration`}
            labelComponent={HiddenLabel}
            placeholder="Description..."
            component={Input}
          />
        )}
      </td>
    </tr>
  )
}

function InstructionalTimeRows({ fields, disciplinesById, ...rest }) {
  return fields.map((field, i) => {
    const row = fields.get(i)
    const discipline = disciplinesById[row.disciplineId]
    if (!discipline) return null

    return (
      <InstructionalTimeRow
        key={field}
        field={field}
        row={row}
        discipline={discipline}
        {...rest}
      />
    )
  })
}

function InstructionalTimeMatrixForm({
  handleSubmit,
  disciplines,
  frequencyTypes,
  durationTypes,
  frequencyColumnHeader,
  durationColumnHeader,
  saved,
  submitting,
  error,
  change,
}) {
  const disciplinesById = keyBy(disciplines, 'id')
  const frequencyOptions = serializeOptions(frequencyTypes)
  const durationOptions = serializeOptions(durationTypes)
  const noneFrequencyTypeId = getNoneTypeId(frequencyTypes)
  const noneDurationTypeId = getNoneTypeId(durationTypes)
  const otherFrequencyTypeId = getOtherTypeId(frequencyTypes)
  const otherDurationTypeId = getOtherTypeId(durationTypes)

  // A discipline that isn't taught has no meaningful class length, so answering
  // "None" once fills in the rest of the row rather than making it busywork.
  const onFrequencyChange = useCallback(
    (field, value) => {
      if (!noneFrequencyTypeId) return
      if (parseSelectedId(value) !== noneFrequencyTypeId) return

      // Deferred so redux-form finishes applying this change before the next
      setTimeout(() => {
        if (noneDurationTypeId)
          change(field + '.durationTypeId', noneDurationTypeId)
        change(field + '.frequencyOtherText', '')
        change(field + '.durationOtherText', '')
      }, 0)
    },
    [change, noneFrequencyTypeId, noneDurationTypeId]
  )

  return (
    <form
      onSubmit={handleSubmit}
      aria-describedby={
        error ? generateInputErrorId(TABLE_GROUP_ERROR_NAME) : undefined
      }
    >
      <table className="instructional-time-table">
        <thead>
          <tr>
            <th scope="col">Discipline</th>
            <th scope="col">{frequencyColumnHeader}</th>
            <th scope="col">{durationColumnHeader}</th>
          </tr>
        </thead>
        <tbody>
          <FieldArray
            name="instructionalTime"
            component={InstructionalTimeRows}
            disciplinesById={disciplinesById}
            frequencyOptions={frequencyOptions}
            durationOptions={durationOptions}
            frequencyColumnHeader={frequencyColumnHeader}
            durationColumnHeader={durationColumnHeader}
            otherFrequencyTypeId={otherFrequencyTypeId}
            otherDurationTypeId={otherDurationTypeId}
            onFrequencyChange={onFrequencyChange}
            rerenderOnEveryChange
          />
        </tbody>
      </table>
      {error && (
        <InputError
          name={TABLE_GROUP_ERROR_NAME}
          error={error}
          touched
          invalid
        />
      )}
      <div className="button-wrapper">
        <SubmitButton {...{ pristine: saved, submitting }}>
          Save Response
        </SubmitButton>
      </div>
    </form>
  )
}

InstructionalTimeMatrixForm.propTypes = propTypes
InstructionalTimeMatrixForm.defaultProps = defaultProps

function buildRows({ disciplines, instructionalFrequencies, classDurations }) {
  const frequencyByDiscipline = keyBy(instructionalFrequencies, (allocation) =>
    get(allocation, 'secondaryResourceable.id')
  )
  const durationByDiscipline = keyBy(classDurations, (allocation) =>
    get(allocation, 'secondaryResourceable.id')
  )

  return disciplines.map((discipline) => {
    const frequency = frequencyByDiscipline[discipline.id]
    const duration = durationByDiscipline[discipline.id]

    return {
      disciplineId: discipline.id,
      frequencyTypeId: get(frequency, 'instructionalFrequencyTypeId') || null,
      frequencyOtherText: get(frequency, 'otherText') || '',
      durationTypeId: get(duration, 'classDurationTypeId') || null,
      durationOtherText: get(duration, 'otherText') || '',
    }
  })
}

function modifyInitialValues({
  disciplines,
  instructionalFrequencies,
  classDurations,
}) {
  return {
    initialValues: {
      instructionalTime: buildRows({
        disciplines,
        instructionalFrequencies,
        classDurations,
      }),
    },
  }
}

function describeIncompleteRows(rows, disciplinesById) {
  return rows
    .filter((row) => !row.frequencyTypeId || !row.durationTypeId)
    .map((row) => get(disciplinesById, [row.disciplineId, 'displayName']))
    .filter(Boolean)
}

function describeMissingOtherText(rows, disciplinesById, otherIds) {
  return rows
    .filter((row) => {
      const needsFrequencyText =
        row.frequencyTypeId === otherIds.frequency &&
        !get(row, 'frequencyOtherText', '').trim()
      const needsDurationText =
        row.durationTypeId === otherIds.duration &&
        !get(row, 'durationOtherText', '').trim()
      return needsFrequencyText || needsDurationText
    })
    .map((row) => get(disciplinesById, [row.disciplineId, 'displayName']))
    .filter(Boolean)
}

function modifyBeforeSubmit({
  disciplines,
  frequencyTypes,
  durationTypes,
  instructionalFrequencies,
  classDurations,
}) {
  const disciplinesById = keyBy(disciplines, 'id')
  const otherIds = {
    frequency: getOtherTypeId(frequencyTypes),
    duration: getOtherTypeId(durationTypes),
  }

  return {
    beforeSubmit: ({ instructionalTime }) => {
      const rows = instructionalTime || []

      const incomplete = describeIncompleteRows(rows, disciplinesById)
      if (incomplete.length)
        throw new SubmissionError({
          _error: `Please select both a frequency and a class duration for: ${incomplete.join(
            ', '
          )}.`,
        })

      const missingText = describeMissingOtherText(
        rows,
        disciplinesById,
        otherIds
      )
      if (missingText.length)
        throw new SubmissionError({
          _error: `Please describe the "Other" selection for: ${missingText.join(
            ', '
          )}.`,
        })

      const newFrequencies = rows.map((row) => ({
        instructionalFrequencyTypeId: row.frequencyTypeId,
        otherText:
          row.frequencyTypeId === otherIds.frequency
            ? row.frequencyOtherText
            : '',
        secondaryResource: {
          disciplines: [{ disciplineId: row.disciplineId }],
        },
      }))

      const newDurations = rows.map((row) => ({
        classDurationTypeId: row.durationTypeId,
        otherText:
          row.durationTypeId === otherIds.duration ? row.durationOtherText : '',
        secondaryResource: {
          disciplines: [{ disciplineId: row.disciplineId }],
        },
      }))

      return {
        instructionalFrequencies: replaceResources({
          old: instructionalFrequencies,
          new: newFrequencies,
        }),
        classDurations: replaceResources({
          old: classDurations,
          new: newDurations,
        }),
      }
    },
  }
}

export default compose(
  modifyProps(modifyBeforeSubmit),
  modifyProps(modifyInitialValues),
  lpForm({
    name: FORM_NAME,
    enableReinitialize: true,
  }),
  persistSubmitSucceeded()
)(InstructionalTimeMatrixForm)
